import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Link,
  Outlet,
  redirect,
  useRouter,
} from '@tanstack/react-router';
import { Button, buttonClass } from './components/Button';
import { Toaster } from './components/Toast';
import { AppShell } from './layout/AppShell';
import { ApiError, setUnauthorizedHandler } from './lib/http';
import { useDesktopMediaQuery, useIsDesktop } from './lib/layout';
import {
  bookingQuery,
  bookingsQuery,
  calendarQuery,
  chatQuery,
  chatsQuery,
  dashboardQuery,
  guideQuery,
  guidesQuery,
  leadQuery,
  leadsQuery,
  meQuery,
  needsYouQuery,
  settingsQuery,
} from './lib/queries';

export interface RouterContext {
  queryClient: QueryClient;
}

function RootLayout() {
  const desktop = useDesktopMediaQuery();
  return (
    <>
      <Outlet />
      <Toaster placement={desktop ? 'bottom' : 'above-tabs'} />
    </>
  );
}

function NotFound() {
  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-4 bg-sand p-6 text-center">
      <h1 className="font-display text-title font-bold">This page is not here.</h1>
      <p className="m-0 text-soft">The link may be old. Everything else is where you left it.</p>
      <Link to="/dashboard" className={buttonClass('secondary', 'md')}>
        Back to the Dashboard
      </Link>
    </div>
  );
}

/** Shown only when a screen takes more than a moment to load; quick loads keep the old screen. */
function Pending() {
  return (
    <div className="flex flex-1 items-center justify-center gap-3 p-10 text-muted" role="status">
      <span aria-hidden="true" className="size-2.5 animate-pulse rounded-full bg-teal" />
      Opening…
    </div>
  );
}

/**
 * A drawer route's wait. On the desktop the drawer's Outlet sits beside the list, so the
 * page-sized Pending would squeeze the list; draw nothing and let the drawer slide in when
 * it is ready. The phone shows the detail as its own page, so it keeps the full Pending.
 */
function DrawerPending() {
  return useIsDesktop() ? null : <Pending />;
}

function RouteError({ error }: { error: unknown }) {
  const router = useRouter();
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="font-display text-head font-bold">This did not load.</h1>
      <p className="m-0 max-w-[420px] text-soft">{message}</p>
      <Button variant="secondary" onClick={() => void router.invalidate()}>
        Try again
      </Button>
    </div>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const id = (value: string) => {
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) throw redirect({ to: '/dashboard' });
  return n;
};

/** One of `list`, or undefined. */
function oneOf<T extends string>(list: readonly T[], value: unknown): T | undefined {
  return typeof value === 'string' && (list as readonly string[]).includes(value) ? (value as T) : undefined;
}

/** The drawer tabs live in `?tab=`; Overview is the clean URL. */
export const LEAD_TABS = ['overview', 'chat', 'guides', 'activity'] as const;
export type LeadTab = (typeof LEAD_TABS)[number];
export const BOOKING_TABS = ['overview', 'chat', 'guides', 'activity'] as const;
export type BookingTab = (typeof BOOKING_TABS)[number];

function tabSearch<T extends string>(list: readonly T[]) {
  return (search: Record<string, unknown>): { tab?: Exclude<T, 'overview'> } => {
    const tab = oneOf(list, search.tab);
    return tab && tab !== 'overview' ? { tab: tab as Exclude<T, 'overview'> } : {};
  };
}

// ---------------------------------------------------------------- public pages

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // Already signed in (or sign-in is open on this host): straight to the Dashboard.
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.fetchQuery({ ...meQuery, staleTime: 0 });
    } catch {
      return;
    }
    throw redirect({ to: '/dashboard' });
  },
  component: lazyRouteComponent(() => import('./screens/LoginScreen'), 'LoginScreen'),
});

const payThanksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pay/thanks',
  component: lazyRouteComponent(() => import('./screens/PayScreens'), 'PayThanks'),
});

const payCancelledRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pay/cancelled',
  component: lazyRouteComponent(() => import('./screens/PayScreens'), 'PayCancelled'),
});

// ---------------------------------------------------------------- guide portal (token in the URL, no login)

const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/g/$token',
  component: lazyRouteComponent(() => import('./portal/PortalLayout'), 'PortalLayout'),
});
const portalTripsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./portal/PortalTrips'), 'PortalTrips'),
});
const portalTripRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'trips/$assignmentId',
  component: lazyRouteComponent(() => import('./portal/PortalTrip'), 'PortalTrip'),
});
const portalCantGoRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'trips/$assignmentId/cant-go',
  component: lazyRouteComponent(() => import('./portal/PortalCantGo'), 'PortalCantGo'),
});
const portalMonthRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: 'month',
  component: lazyRouteComponent(() => import('./portal/PortalMonth'), 'PortalMonth'),
});

// ---------------------------------------------------------------- Seth's dashboard (signed in)

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppShell,
  errorComponent: RouteError,
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw redirect({ to: '/login' });
      throw err;
    }
  },
});

/** The old Home address opens the Dashboard. */
const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  loader: ({ context }) => context.queryClient.ensureQueryData(dashboardQuery),
  component: lazyRouteComponent(() => import('./screens/DashboardScreen'), 'DashboardScreen'),
});

const leadsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/leads',
  loader: ({ context }) => context.queryClient.ensureQueryData(leadsQuery),
  component: lazyRouteComponent(() => import('./screens/LeadsScreen'), 'LeadsScreen'),
});
/** One lead: a drawer over the list on desktop, its own page on the phone. */
const leadRoute = createRoute({
  getParentRoute: () => leadsRoute,
  path: '$id',
  validateSearch: tabSearch(LEAD_TABS),
  loader: async ({ context, params }) => {
    const lead = await context.queryClient.ensureQueryData(leadQuery(id(params.id)));
    // The Chat tab is one tap away; have it ready.
    if (lead.conversationId) void context.queryClient.prefetchQuery(chatQuery(lead.conversationId));
    return lead;
  },
  component: lazyRouteComponent(() => import('./screens/LeadDetailScreen'), 'LeadDetailScreen'),
  pendingComponent: DrawerPending,
});

const needsYouRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/needs-you',
  loader: ({ context }) => context.queryClient.ensureQueryData(needsYouQuery),
  component: lazyRouteComponent(() => import('./screens/NeedsYouScreen'), 'NeedsYouScreen'),
});
/** The selected item lives in the URL; the parent screen reads it. */
const needsYouItemRoute = createRoute({ getParentRoute: () => needsYouRoute, path: '$id' });

const chatsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats',
  loader: async ({ context }) => {
    const view = await context.queryClient.ensureQueryData(chatsQuery);
    // On the desktop the first chat opens when none is picked yet.
    const first = view.items[0];
    if (first) await context.queryClient.ensureQueryData(chatQuery(first.id));
    return view;
  },
  component: lazyRouteComponent(() => import('./screens/ChatsScreen'), 'ChatsScreen'),
});
const chatRoute = createRoute({
  getParentRoute: () => chatsRoute,
  path: '$id',
  /** The guide thread shown beside a guest's, by its conversation id. */
  validateSearch: (search: Record<string, unknown>): { guide?: number } => {
    const n = Number(search.guide);
    return Number.isInteger(n) && n > 0 ? { guide: n } : {};
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(chatQuery(id(params.id))),
});

const bookingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/bookings',
  loader: ({ context }) => context.queryClient.ensureQueryData(bookingsQuery),
  component: lazyRouteComponent(() => import('./screens/BookingsScreen'), 'BookingsScreen'),
});
const bookingRoute = createRoute({
  getParentRoute: () => bookingsRoute,
  path: '$id',
  validateSearch: tabSearch(BOOKING_TABS),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(bookingQuery(id(params.id))),
  component: lazyRouteComponent(() => import('./screens/BookingDetailScreen'), 'BookingDetailScreen'),
  pendingComponent: DrawerPending,
});

const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/calendar',
  validateSearch: (search: Record<string, unknown>): { week?: string } =>
    typeof search.week === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search.week) ? { week: search.week } : {},
  loaderDeps: ({ search }) => ({ week: search.week }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(calendarQuery(deps.week)),
  component: lazyRouteComponent(() => import('./screens/CalendarScreen'), 'CalendarScreen'),
});

/** A booking opened from the calendar: the same drawer, over the calendar, keeping the week. */
const calendarBookingRoute = createRoute({
  getParentRoute: () => calendarRoute,
  path: '$id',
  validateSearch: tabSearch(BOOKING_TABS),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(bookingQuery(id(params.id))),
  component: lazyRouteComponent(() => import('./screens/BookingDetailScreen'), 'CalendarBookingScreen'),
  pendingComponent: DrawerPending,
});

export const GUIDE_VIEWS = ['team', 'whatsapp', 'order', 'pay', 'offers'] as const;
export type GuideView = (typeof GUIDE_VIEWS)[number];

const guidesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/guides',
  validateSearch: (search: Record<string, unknown>): { view?: GuideView } =>
    (GUIDE_VIEWS as readonly string[]).includes(String(search.view)) ? { view: search.view as GuideView } : {},
  // The cards show as soon as the list is in; the first guide's panel fills in behind them.
  loader: async ({ context }) => {
    const view = await context.queryClient.ensureQueryData(guidesQuery);
    const first = view.guides[0];
    if (first) void context.queryClient.prefetchQuery(guideQuery(first.id));
    return view;
  },
  component: lazyRouteComponent(() => import('./screens/GuidesScreen'), 'GuidesScreen'),
});
const guideRoute = createRoute({
  getParentRoute: () => guidesRoute,
  path: '$id',
  loader: ({ context, params }) => context.queryClient.ensureQueryData(guideQuery(id(params.id))),
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/$tab', params: { tab: 'setup' } });
  },
});

/**
 * Settings, one tab per kind of thing Seth looks after; each tab's sub-tabs ride in `?view=`.
 * The tabs before November 2026 (Rules, Booking, Guides…) still open, at their new home.
 */
export const SETTINGS_VIEWS = {
  setup: ['overview', 'questions'],
  prices: ['list', 'payment'],
  pickups: ['hotels', 'missing', 'times'],
  cenotes: ['list', 'bases'],
  keta: ['facts', 'waiting', 'rules', 'retired'],
  account: ['connections', 'history', 'spending'],
} as const;
export type SettingsTab = keyof typeof SETTINGS_VIEWS;
const SETTINGS_TABS = Object.keys(SETTINGS_VIEWS) as SettingsTab[];
const LEGACY_TABS: Record<string, { tab: SettingsTab; view?: string } | 'guides'> = {
  rules: { tab: 'keta', view: 'rules' },
  knowledge: { tab: 'keta', view: 'facts' },
  booking: { tab: 'pickups', view: 'times' },
  guides: 'guides',
  connections: { tab: 'account', view: 'connections' },
  history: { tab: 'account', view: 'history' },
  spending: { tab: 'account', view: 'spending' },
};

/** The view a tab opens on: the one asked for when it belongs to the tab, else the tab's first. */
export function settingsView(tab: SettingsTab, view: string | undefined): string | undefined {
  const views: readonly string[] = SETTINGS_VIEWS[tab];
  return view && views.includes(view) ? view : views[0];
}

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/$tab',
  params: {
    parse: (raw: { tab: string }) => ({ tab: (SETTINGS_TABS as readonly string[]).includes(raw.tab) ? (raw.tab as SettingsTab) : ('setup' as SettingsTab) }),
    stringify: (p: { tab: SettingsTab }) => ({ tab: p.tab }),
  },
  validateSearch: (search: Record<string, unknown>): { view?: string } => (typeof search.view === 'string' ? { view: search.view } : {}),
  beforeLoad: ({ location }) => {
    const raw = location.pathname.split('/')[2] ?? '';
    const legacy = LEGACY_TABS[raw];
    if (legacy === 'guides') throw redirect({ to: '/guides', search: { view: 'order' } });
    if (legacy) throw redirect({ to: '/settings/$tab', params: { tab: legacy.tab }, search: legacy.view ? { view: legacy.view } : {} });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(settingsQuery),
  component: lazyRouteComponent(() => import('./screens/SettingsScreen'), 'SettingsScreen'),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  payThanksRoute,
  payCancelledRoute,
  portalRoute.addChildren([portalTripsRoute, portalTripRoute, portalCantGoRoute, portalMonthRoute]),
  appRoute.addChildren([
    indexRoute,
    dashboardRoute,
    leadsRoute.addChildren([leadRoute]),
    needsYouRoute.addChildren([needsYouItemRoute]),
    chatsRoute.addChildren([chatRoute]),
    bookingsRoute.addChildren([bookingRoute]),
    calendarRoute.addChildren([calendarBookingRoute]),
    guidesRoute.addChildren([guideRoute]),
    settingsIndexRoute,
    settingsRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // React Query owns caching; the router only asks it to warm up.
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: RouteError,
    defaultPendingComponent: Pending,
    defaultPendingMs: 700,
    defaultPendingMinMs: 300,
    scrollRestoration: false,
  });

  // A lapsed session anywhere lands on the login page, once, without a reload.
  setUnauthorizedHandler(() => {
    const path = router.state.location.pathname;
    if (path === '/login' || path.startsWith('/g/') || path.startsWith('/pay/')) return;
    queryClient.clear();
    void router.navigate({ to: '/login' });
  });

  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
